import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getAuthToken } from "../../utils/api";
import authService from "../../services/authService";

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, adminOnly }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Idle session timeout — logout after 30 min of no user activity
  useEffect(() => {
    const handleLogout = () => {
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      navigate("/login", { replace: true });
    };

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(handleLogout, SESSION_TIMEOUT_MS);
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();

      if (!token) {
        setIsAuthorized(false);
        setIsChecking(false);
        return;
      }

      const verify = async (t: string) =>
        fetch(`${process.env.REACT_APP_BASE_URL}/users/me/`, {
          headers: { Authorization: `Bearer ${t}` },
        });

      try {
        let response = await verify(token);

        if (response.status === 401) {
          // Token expired — try silent refresh once
          try {
            const newToken = await authService.refreshToken();
            response = await verify(newToken);
          } catch {
            // Refresh failed — force re-login
          }
        }

        if (response.ok) {
          setIsAuthorized(true);
        } else {
          localStorage.removeItem("authToken");
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.removeItem("authToken");
        setIsAuthorized(false);
      }

      setIsChecking(false);
    };

    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly) {
    const storedUser =
      JSON.parse(localStorage.getItem("docfi_user") || "null") ||
      JSON.parse(localStorage.getItem("user") || "null");
    if (!storedUser?.is_staff) {
      return <Navigate to="/encounters" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
